import React, { useContext, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Filter,
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
  RefreshCw,
  Gift,
  Landmark,
} from "lucide-react";
import { listAllGiftCardRedemption } from "../../reducers/payouts.slice";
import { getAllWalletTransaction } from "../../reducers/wallet.slice";
import {
  createPlaidIdentityVerification,
  getPlaidIdentityVerificationStatus,
  retryPlaidIdentityVerification,
} from "../../reducers/authentication.slice";
import { convertDateFormatInUtc, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { getUsers } from "../../utils/getUser";
import { RSC_PRIVACY_POLICY } from "../../constants/privacyPolicy";
import PaypalLogo from "../../assests/images/Paypal.png";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../components/ui/tooltip";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import FieldBlock from "../../components/shared/FieldBlock";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import DataTable from "../../components/shared/DataTable";
import useMediaQuery from "../../components/shared/useMediaQuery";

const WALLET_SESSION_KEYS = {
  rowsPerPage: "walletRowsPerPage",
  selectedTypes: "walletSelectedTypes",
  sortDirection: "walletSortDirection",
};

const RETRYABLE_PLAID_STATUSES = ["failed", "expired", "canceled", "pending_review"];
const PLAID_CONSENT_POLICY_VERSION = "2026-07-08";
const getPlaidConsentStorageKey = (userId) =>
  `plaidIdentityConsentAccepted:${userId || "anonymous"}:${PLAID_CONSENT_POLICY_VERSION}`;

const WITHDRAW_METHODS = [
  {
    key: "gift-card",
    icon: Gift,
    title: "Purchase Gift Cards",
    badge: "Recommended — Save 10% or more",
    accent: "linear-gradient(135deg, rgba(175,88,242,0.25), rgba(175,88,242,0.06))",
  },
  {
    key: "bank",
    icon: Landmark,
    title: "Withdraw to Bank Account",
    accent: "linear-gradient(135deg, rgba(232,184,75,0.22), rgba(232,184,75,0.05))",
  },
  {
    key: "paypal",
    icon: null,
    title: "Withdraw to PayPal",
    accent: "linear-gradient(135deg, rgba(34,144,255,0.22), rgba(34,144,255,0.05))",
  },
];

const MemberWalletPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = sessionStorage.getItem(WALLET_SESSION_KEYS.rowsPerPage);
    return stored ? JSON.parse(stored) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("gift-card");
  const { walletSummary, setIsWalletRefresh, isWalletRefresh, showToast } = useContext(AuthContext);
  const [walletDetail, setWalletDetail] = useState({});
  const [animatedBalance, setAnimatedBalance] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const stored = sessionStorage.getItem(WALLET_SESSION_KEYS.selectedTypes);
    return stored ? JSON.parse(stored) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem(WALLET_SESSION_KEYS.sortDirection) || null;
  });

  const [showWithdrawOptions, setShowWithdrawOptions] = useState(false);
  const [showPlaidVerificationDialog, setShowPlaidVerificationDialog] = useState(false);
  const [plaidVerificationDetails, setPlaidVerificationDetails] = useState(null);

  const user = getUsers();
  const [hasAcceptedPlaidTerms, setHasAcceptedPlaidTerms] = useState(() => {
    return localStorage.getItem(getPlaidConsentStorageKey(user?.id)) === "true";
  });
  const [plaidTermsChecked, setPlaidTermsChecked] = useState(false);

  const isInitialSearchRender = useRef(true);

  const getAllPayment = (
    page = currentPage,
    pageSize = rowsPerPage,
    transactionTypes = selectedTypes,
    amountSortDirection = sortDirection,
    search = debouncedSearch
  ) => {
    setLoading(true);
    dispatch(
      getAllWalletTransaction({
        skip: page * pageSize,
        limit: pageSize,
        transaction_type: transactionTypes,
        search: search?.trim(),
        sort_by: amountSortDirection ? "AMOUNT" : "CREATED_AT",
        sort_order: amountSortDirection || "DESC",
      })
    ).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const walletTransactions = Array.isArray(res?.payload) ? res?.payload : res?.payload?.data || [];
      setTotalRows(res?.payload?.total || walletTransactions.length);

      const formattedPayouts = walletTransactions.map((item) => {
        const createdDateOnly = convertDateFormatInUtc(item?.created_at, "MMM D, YYYY");
        const createdTimeOnly = convertDateFormatInUtc(item?.created_at, "h:mm A");
        const totalAmount = formatNumberToDollar(item?.amount);
        const calculatedBoost =
          ((item?.transaction_ref_details?.actual_amount - item?.transaction_ref_details?.discounted_amount) /
            item?.transaction_ref_details?.actual_amount) *
          100;
        return { ...item, createdDateOnly, createdTimeOnly, totalAmount, calculatedBoost };
      });
      setLoading(false);
      setIsWalletRefresh(!isWalletRefresh);
      setFilteredData(formattedPayouts);
    });
  };

  useEffect(() => {
    getAllPayment(0, rowsPerPage, selectedTypes, sortDirection, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialSearchRender.current) {
      isInitialSearchRender.current = false;
      return;
    }
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, sortDirection, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Counts the wallet balance up from 0 as it lands, matching the artifact's
  // count-up (1300ms, cubic ease-out) — skipped entirely for users who've
  // asked the OS for reduced motion.
  useEffect(() => {
    const target = walletSummary?.current_balance;
    if (target == null) return;

    const prefersReducedMotion =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (prefersReducedMotion) {
      setAnimatedBalance(target);
      return;
    }

    let frameId;
    const duration = 1300;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedBalance(target * eased);
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [walletSummary?.current_balance]);

  const maskSensitiveNumber = (number) => {
    if (!number) return "****";
    const lastFour = number.slice(-4);
    const maskedPortion = "*".repeat(Math.max(0, number.length - 4));
    return `${maskedPortion}${lastFour}`;
  };

  useEffect(() => {
    setLoading(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setLoading(false);
        return;
      }
      const totalSaved = parseFloat(
        res?.payload
          ?.reduce((sum, item) => (item?.status !== "REJECTED" ? sum + (item.actual_amount - item.discounted_amount) : sum), 0)
          .toFixed(2)
      );
      const totalGiftCards = res?.payload?.filter((item) => item.status !== "REJECTED")?.length || 0;
      setWalletDetail({ totalSaved, totalGiftCards });
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatRetailerName = (retailer) => retailer?.replace(/_/g, " ");

  const descriptionText = (row) => {
    if (row?.transaction_ref_type === "REDEMPTION_REQUEST") {
      return `Gift Card - ${formatRetailerName(row?.transaction_ref_details?.gift_card_details?.retailer)}`;
    }
    if (row?.transaction_ref_type === "PAYOUT_REQUEST") {
      return `Shipment - ${row?.transaction_ref_details?.shipment_details?.id}`;
    }
    if (row?.transaction_ref_type === "WITHDRAWAL_REQUEST") {
      const isPaypal = row?.transaction_ref_details?.payment_method_details?.payment_method === "PayPal";
      return isPaypal
        ? row?.transaction_ref_details?.payment_method_details?.payment_method
        : `Bank Transfer - ${row?.transaction_ref_details?.payment_method_details?.account_last_four ? `****${row.transaction_ref_details.payment_method_details.account_last_four}` : maskSensitiveNumber(row?.transaction_ref_details?.payment_method_details?.account_number)}`;
    }
    return "-";
  };

  const columns = [
    { key: "id", label: "ID", maxWidth: 56 },
    {
      key: "createdDateOnly",
      label: "Date",
      maxWidth: 90,
      render: (row) => (
        <div style={{ lineHeight: 1.4 }}>
          <div>{row.createdDateOnly}</div>
          {row.createdTimeOnly && <div style={{ fontSize: 11, color: "#c3c8cc" }}>{row.createdTimeOnly}</div>}
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      render: (row) =>
        row?.transaction_ref_type === "REDEMPTION_REQUEST" && row?.transaction_type !== "CREDIT" ? (
          <div style={{ lineHeight: 1.4 }}>
            <div>
              <span style={{ textDecoration: "line-through", color: "#c3c8cc", marginRight: 6, fontSize: 11 }}>
                {formatNumberToDollar(row?.transaction_ref_details?.actual_amount)}
              </span>
              <span style={{ fontWeight: 700, color: "#fff" }}>
                {formatNumberToDollar(row?.transaction_ref_details?.discounted_amount)}
              </span>
            </div>
            <div style={{ fontSize: 10.5, color: "#4caf50", fontWeight: 700 }}>
              Saved {row?.calculatedBoost?.toFixed?.(0) ?? row?.calculatedBoost}%
            </div>
          </div>
        ) : (
          <span style={{ fontWeight: 700, color: row?.transaction_type === "CREDIT" ? "#4caf50" : "#fff" }}>
            {row?.transaction_type === "CREDIT" ? "+" : row?.transaction_type === "DEBIT" ? "−" : ""}
            {row?.totalAmount}
          </span>
        ),
    },
    {
      key: "description",
      label: "Description",
      maxWidth: 120,
      render: (row) => {
        const text = descriptionText(row);
        if (row?.transaction_ref_type === "WITHDRAWAL_REQUEST") {
          const isPaypal = row?.transaction_ref_details?.payment_method_details?.payment_method === "PayPal";
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span style={{ cursor: "default" }}>{text}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {isPaypal
                    ? "Your funds are on the way to your PayPal account! PayPal withdrawals are sent within 3 business days."
                    : "Your funds are on the way to your bank account! ACH withdrawal requests can take up to 3 business days to initiate, and are delivered between 1-5 business days."}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }
        return <span title={text}>{text}</span>;
      },
    },
    {
      key: "transaction_type",
      label: "Type",
      maxWidth: 68,
      render: (row) => (
        <StatusBadge status={row?.transaction_type} label={row?.transaction_type === "CREDIT" ? "Credited" : row?.transaction_type === "DEBIT" ? "Debited" : undefined} />
      ),
    },
  ];

  const handleTypeChange = (type, checked) => {
    const updatedTypes = checked ? [...selectedTypes, type] : selectedTypes.filter((t) => t !== type);
    setSelectedTypes(updatedTypes);
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, updatedTypes, sortDirection, debouncedSearch);
  };

  const handleSortAscending = () => {
    setSortDirection("asc");
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, "asc", debouncedSearch);
  };
  const handleSortDescending = () => {
    setSortDirection("desc");
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, "desc", debouncedSearch);
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    getAllPayment(newPage, rowsPerPage, selectedTypes, sortDirection, debouncedSearch);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem(WALLET_SESSION_KEYS.rowsPerPage, JSON.stringify(newRowsPerPage));
    setCurrentPage(0);
    getAllPayment(0, newRowsPerPage, selectedTypes, sortDirection, debouncedSearch);
  };

  useEffect(() => {
    sessionStorage.setItem(WALLET_SESSION_KEYS.selectedTypes, JSON.stringify(selectedTypes));
  }, [selectedTypes]);

  useEffect(() => {
    if (sortDirection) {
      sessionStorage.setItem(WALLET_SESSION_KEYS.sortDirection, sortDirection);
    } else {
      sessionStorage.removeItem(WALLET_SESSION_KEYS.sortDirection);
    }
  }, [sortDirection]);

  const getNormalizedPlaidStatus = (status) => String(status || "").toLowerCase();

  const isPlaidVerificationRetryable = (details) => {
    const latestStatus = getNormalizedPlaidStatus(details?.status);
    return (
      RETRYABLE_PLAID_STATUSES.includes(latestStatus) && details?.attempts_remaining > 0 && !details?.manual_review_required
    );
  };

  const handleWithdrawClick = async () => {
    setLoading(true);
    const response = await dispatch(getPlaidIdentityVerificationStatus());
    setLoading(false);

    if (response?.error && Object.keys(response?.error)?.length > 0) {
      showToast(response?.error?.message, "error");
      return;
    }

    const verificationDetails = response?.payload;
    if (verificationDetails?.is_plaid_verified) {
      setShowWithdrawOptions(true);
      return;
    }

    setPlaidVerificationDetails(verificationDetails);
    setShowPlaidVerificationDialog(true);
  };

  const handleCheckPlaidVerificationStatus = async () => {
    setLoading(true);
    const response = await dispatch(getPlaidIdentityVerificationStatus());
    setLoading(false);

    if (response?.error && Object.keys(response?.error)?.length > 0) {
      showToast(response?.error?.message, "error");
      return;
    }

    const verificationDetails = response?.payload;
    setPlaidVerificationDetails(verificationDetails);

    if (verificationDetails?.is_plaid_verified) {
      setShowPlaidVerificationDialog(false);
      setShowWithdrawOptions(true);
      showToast("Identity verification approved. You can withdraw now.", "success");
      return;
    }

    if (verificationDetails?.manual_review_required) {
      showToast("Your identity verification is under manual review.", "warning");
      return;
    }

    if (isPlaidVerificationRetryable(verificationDetails)) {
      showToast("Identity verification was not completed successfully. You can retry now.", "warning");
      return;
    }

    showToast("Identity verification is still in progress.", "info");
  };

  const openPlaidVerificationUrl = (shareableUrl) => {
    if (!shareableUrl) {
      showToast("Identity verification link is not available yet.", "error");
      return;
    }
    window.open(shareableUrl, "_blank", "noopener,noreferrer");
  };

  const needsPlaidTermsAcceptance = () =>
    !plaidVerificationDetails?.identity_verification_id && !hasAcceptedPlaidTerms;

  const handleStartIdentityVerification = async () => {
    const latestStatus = getNormalizedPlaidStatus(plaidVerificationDetails?.status);

    if (plaidVerificationDetails?.manual_review_required) {
      showToast("Your identity verification is under manual review.", "warning");
      return;
    }

    if (!plaidVerificationDetails?.identity_verification_id) {
      if (needsPlaidTermsAcceptance() && !plaidTermsChecked) {
        showToast("Please accept the RSC Privacy Policy and Plaid terms to continue.", "warning");
        return;
      }

      setLoading(true);
      const response = await dispatch(createPlaidIdentityVerification({ gave_consent: true }));
      setLoading(false);

      if (response?.error && Object.keys(response?.error)?.length > 0) {
        showToast(response?.error?.message, "error");
        return;
      }

      localStorage.setItem(getPlaidConsentStorageKey(user?.id), "true");
      setHasAcceptedPlaidTerms(true);
      setPlaidTermsChecked(true);
      setPlaidVerificationDetails(response?.payload);
      openPlaidVerificationUrl(response?.payload?.shareable_url);
      return;
    }

    if (isPlaidVerificationRetryable(plaidVerificationDetails)) {
      setLoading(true);
      const response = await dispatch(retryPlaidIdentityVerification());
      setLoading(false);

      if (response?.error && Object.keys(response?.error)?.length > 0) {
        showToast(response?.error?.message, "error");
        return;
      }

      setPlaidVerificationDetails(response?.payload);
      openPlaidVerificationUrl(response?.payload?.shareable_url);
      return;
    }

    if (plaidVerificationDetails?.shareable_url) {
      openPlaidVerificationUrl(plaidVerificationDetails.shareable_url);
      return;
    }

    if (RETRYABLE_PLAID_STATUSES.includes(latestStatus)) {
      showToast("Identity verification retry is not available right now.", "warning");
      return;
    }

    showToast("Please complete identity verification before withdrawing.", "warning");
  };

  const getPlaidVerificationMessage = () => {
    const latestStatus = getNormalizedPlaidStatus(plaidVerificationDetails?.status);

    if (plaidVerificationDetails?.manual_review_required) {
      return "Your identity verification is under manual review. You can withdraw after verification is approved.";
    }
    if (RETRYABLE_PLAID_STATUSES.includes(latestStatus) && plaidVerificationDetails?.attempts_remaining === 0) {
      return plaidVerificationDetails?.next_available_retry_date
        ? `Your identity verification attempt limit has been reached. You can retry after ${plaidVerificationDetails.next_available_retry_date}.`
        : "Your identity verification attempt limit has been reached. Please try again later.";
    }
    if (!plaidVerificationDetails?.identity_verification_id) {
      return needsPlaidTermsAcceptance()
        ? "Review and accept the RSC Privacy Policy and Plaid terms before starting identity verification."
        : "You need to verify your identity before making a withdrawal.";
    }
    if (isPlaidVerificationRetryable(plaidVerificationDetails)) {
      return "Your previous identity verification was not completed successfully. Please retry identity verification before withdrawing.";
    }
    return "You already have an identity verification in progress. Please complete it before withdrawing.";
  };

  const getPlaidVerificationActionLabel = () => {
    if (!plaidVerificationDetails?.identity_verification_id) {
      return needsPlaidTermsAcceptance() ? "Accept & Start Verification" : "Start Verification";
    }
    if (isPlaidVerificationRetryable(plaidVerificationDetails)) {
      return "Retry Verification";
    }
    return "Continue Verification";
  };

  const canStartPlaidVerification = () =>
    !plaidVerificationDetails?.manual_review_required &&
    !(
      RETRYABLE_PLAID_STATUSES.includes(getNormalizedPlaidStatus(plaidVerificationDetails?.status)) &&
      plaidVerificationDetails?.attempts_remaining === 0
    ) &&
    (!plaidVerificationDetails?.identity_verification_id ||
      isPlaidVerificationRetryable(plaidVerificationDetails) ||
      Boolean(plaidVerificationDetails?.shareable_url));

  const handleSelectedMethod = () => {
    if (selectedMethod === "gift-card") {
      navigate("/dashboard/wallet/gift-cards");
    } else if (selectedMethod === "bank") {
      navigate("/dashboard/wallet/bank-account");
    } else if (selectedMethod === "paypal") {
      navigate("/dashboard/wallet/paypal-account");
    }
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedTypes);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="My Wallet" subtitle="You can see your wallet balance from shipments here." />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {/* Left column: balance + gift cards, matching the artifact's stacked layout */}
        <div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", marginRight: isMobile ? 0 : 20, marginBottom: 20, minWidth: 0 }}>
          <div
            style={{
              borderRadius: 18,
              padding: isMobile ? "18px 16px" : "22px 24px",
              background: "linear-gradient(135deg, rgba(76,175,80,0.16), #161618 62%)",
              border: "1px solid #2a2a2e",
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.2em", color: "#c3c8cc" }}>
              WALLET BALANCE
            </div>
            <div
              style={{
                fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                fontSize: isMobile ? 34 : 44,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                margin: "6px 0",
                backgroundImage: "linear-gradient(100deg, #4caf50, #22d3ee)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {formatNumberToDollar(animatedBalance)}
            </div>
            <div style={{ fontSize: 12, color: "#c3c8cc", marginBottom: 16 }}>
              Available to withdraw or spend on gift cards
            </div>
            <Button onClick={handleWithdrawClick} style={{ width: "100%" }}>
              Withdraw
            </Button>
          </div>

          <div
            style={{
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.06)",
              overflow: "hidden",
              flex: 1,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 18px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Gift Cards</span>
              <span style={{ fontSize: 11, color: "#c3c8cc" }}>buy discounted retailer cards</span>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <FieldBlock label="PURCHASED" value={walletDetail?.totalGiftCards ?? "—"} color="#af58f2" />
                <FieldBlock
                  label="SAVED"
                  value={walletDetail?.totalSaved != null ? formatNumberToDollar(walletDetail.totalSaved) : "—"}
                  color="#4caf50"
                />
              </div>
              <Button variant="outline" onClick={() => navigate("/dashboard/wallet/gift-cards")} style={{ width: "100%" }}>
                View Gift Cards
              </Button>
            </div>
          </div>
        </div>

        {/* Right column: Wallet History — needs more room than the artifact's
            1.3:1 mockup ratio gives it once you add real ID/Type columns the
            mockup doesn't render, so it gets the larger share here instead. */}
        <div
          style={{
            flex: "1.3 1 420px",
            minWidth: 0,
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.06)",
            overflow: "hidden",
            marginBottom: 20,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 12 }}>Wallet History</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", gap: isMobile ? 10 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8, flex: isMobile ? "1 1 0" : "0 0 auto", minWidth: 0, gap: isMobile ? 8 : 10 }}>
                {!isMobile && (
                  <RefreshButton
                    onRefresh={() => getAllPayment(currentPage, rowsPerPage, selectedTypes, sortDirection, debouncedSearch)}
                  />
                )}
                <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
                  <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} setDebouncedTerm={setDebouncedSearch} />
                </div>
              </div>

              <div style={{ marginLeft: isMobile ? 0 : 20, marginBottom: 8, flexShrink: 0 }}>
                <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" style={{ maxWidth: "100%" }}>
                      <Filter size={13} style={{ marginRight: 6 }} />
                      Filter & Sort
                      {activeFilterCount > 0 && (
                        <span
                          style={{
                            marginLeft: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 16,
                            height: 16,
                            borderRadius: 8,
                            background: "#00ADB5",
                            color: "#000",
                            fontSize: 10,
                            fontWeight: 800,
                            padding: "0 4px",
                          }}
                        >
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="tw-w-56">
                    <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                    {["CREDIT", "DEBIT"].map((type) => (
                      <DropdownMenuCheckboxItem
                        key={type}
                        checked={selectedTypes.includes(type)}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(checked) => handleTypeChange(type, checked)}
                      >
                        {type}
                      </DropdownMenuCheckboxItem>
                    ))}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem active={sortDirection === "asc"} onClick={handleSortAscending}>
                      <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                      Sort by Amount (Low to High)
                    </DropdownMenuItem>
                    <DropdownMenuItem active={sortDirection === "desc"} onClick={handleSortDescending}>
                      <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                      Sort by Amount (High to Low)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <DataTable
              columns={columns}
              rows={filteredData}
              totalRows={totalRows}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={(newPage) => handleChangePage(newPage)}
              onRowsPerPageChange={(newValue) => handleChangeRowsPerPage(newValue)}
              loading={loading}
              minWidth={0}
            />
          </div>
        </div>
      </div>

      <Dialog open={showWithdrawOptions} onOpenChange={setShowWithdrawOptions}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose Withdraw Method</DialogTitle>
            <DialogDescription>
              Confirm the bank account and the amount you have submitted for withdrawal.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {WITHDRAW_METHODS.map((method) => {
              const Icon = method.icon;
              const active = selectedMethod === method.key;
              return (
                <button
                  key={method.key}
                  type="button"
                  onClick={() => setSelectedMethod(method.key)}
                  style={{
                    display: "flex",
                    alignItems: isMobile ? "flex-start" : "center",
                    padding: 14,
                    borderRadius: 12,
                    background: "#141416",
                    border: active ? "1px solid rgba(255,255,255,0.4)" : "1px solid transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 8,
                      background: method.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: isMobile ? 12 : 16,
                      flexShrink: 0,
                    }}
                  >
                    {Icon ? <Icon size={22} color="#fff" /> : <img src={PaypalLogo} alt="paypal" height={22} width={22} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "#fff" }}>{method.title}</div>
                    {method.badge && (
                      <div
                        style={{
                          marginTop: 4,
                          display: "inline-block",
                          background: "rgba(76,175,80,0.15)",
                          color: "#4caf50",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 6,
                        }}
                      >
                        {method.badge}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button onClick={handleSelectedMethod}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPlaidVerificationDialog} onOpenChange={setShowPlaidVerificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Identity</DialogTitle>
            <DialogDescription>{getPlaidVerificationMessage()}</DialogDescription>
          </DialogHeader>

          {needsPlaidTermsAcceptance() && (
            <>
              <div
                className="member-scroll"
                style={{
                  maxHeight: 260,
                  overflowY: "auto",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 8,
                  padding: 14,
                  background: "rgba(255,255,255,0.04)",
                  fontSize: 12.5,
                  color: "#c3c8cc",
                  lineHeight: 1.6,
                }}
              >
                <ReactMarkdown>{RSC_PRIVACY_POLICY}</ReactMarkdown>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", marginTop: 14, fontSize: 12.5, color: "#c3c8cc", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={plaidTermsChecked}
                  onChange={(e) => setPlaidTermsChecked(e.target.checked)}
                  style={{ marginRight: 10, marginTop: 3 }}
                />
                I have read and agree to the RSC Privacy Policy and Plaid identity verification terms.
              </label>
            </>
          )}

          {plaidVerificationDetails?.attempt_number && (
            <div style={{ fontSize: 11.5, color: "#c3c8cc", marginTop: 12 }}>
              Attempt {plaidVerificationDetails.attempt_number}
              {typeof plaidVerificationDetails?.attempts_remaining === "number"
                ? ` — ${plaidVerificationDetails.attempts_remaining} retries remaining`
                : ""}
            </div>
          )}

          <DialogFooter style={{ flexWrap: "wrap" }}>
            <Button variant="outline" onClick={() => setShowPlaidVerificationDialog(false)}>
              Close
            </Button>
            {plaidVerificationDetails?.identity_verification_id && (
              <div style={{ marginLeft: 10 }}>
                <Button variant="outline" onClick={handleCheckPlaidVerificationStatus}>
                  <RefreshCw size={13} style={{ marginRight: 6 }} />
                  Check Status
                </Button>
              </div>
            )}
            {canStartPlaidVerification() && (
              <div style={{ marginLeft: 10 }}>
                <Button
                  onClick={handleStartIdentityVerification}
                  disabled={needsPlaidTermsAcceptance() && !plaidTermsChecked}
                >
                  {getPlaidVerificationActionLabel()}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberWalletPage;
